import { EditorData, StructuredDescription } from "@/models/EditorData"
import { ReferencedResourcePreviews } from "@/models/Search"
import { Status } from "@/models/Status"
import { TaskTypeOption } from "@/models/Task"
import { typeCreateById } from "@/models/Type"
import { $t } from "@/plugins/i18n"

export const syncDescriptionHtml = ({
  referencedResourcePreviews,
  structuredDescription
}: {
  referencedResourcePreviews?: ReferencedResourcePreviews
  structuredDescription: StructuredDescription
}) => {
  const editorData = new EditorData()

  editorData.setHtml(StructuredDescription.updateHtmlByJson(structuredDescription))

  if (!referencedResourcePreviews || !Object.keys(referencedResourcePreviews).length) {
    return editorData.getHtmlText()
  }

  referencedResourcePreviews.items?.forEach(item => {
    if (!item?.id) return
    const { id, title, status, itemType } = item
    editorData.updateHtmlFromRefers(
      id,
      {
        title,
        status,
        itemType,
        statusParsed: Status.createById({ id: status }),
        itemTypeParsed: typeCreateById({ id: itemType }),
        sequenceNumber: item.sequenceNumber,
        linkText: `#${item.sequenceNumber} - ${item.title}`,
        configType: "ITEM"
      },
      item.convertedFromTask ? item.id : undefined
    )
  })

  referencedResourcePreviews.tasks?.forEach(task => {
    if (!task) return
    const { id, name, order, type, estimatedHours } = task
    const typeParsed = {
      id: type,
      label: $t(`task.types.${type.toLocaleLowerCase()}`)
    } as TaskTypeOption
    editorData.updateHtmlFromRefers(id, {
      name,
      order,
      type,
      estimatedHours,
      typeParsed
    })
  })

  referencedResourcePreviews.milestones?.forEach(milestone => {
    if (!milestone) return
    const { id, title, endDate } = milestone
    editorData.updateHtmlFromRefers(id, {
      title,
      endDate
    })
  })

  referencedResourcePreviews.epics?.forEach(epic => {
    if (!epic) return
    const { id, name, percentComplete } = epic
    editorData.updateHtmlFromRefers(
      id,
      {
        name,
        percentComplete,
        configType: "EPIC",
        linkText: `#E - ${epic.sequenceNumber} ${epic.name}`,
        sequenceNumber: epic.sequenceNumber
      },
      epic.convertedFromItemId
    )
  })

  referencedResourcePreviews.ideas?.forEach(idea => {
    if (!idea) return
    const { id, title, status, deleted, sequenceNumber } = idea
    editorData.updateHtmlFromRefers(id, {
      title,
      status,
      deleted,
      sequenceNumber,
      linkText: `#I - ${sequenceNumber} ${title}`
    })
  })

  referencedResourcePreviews.projects?.forEach(project => {
    if (!project) return
    const { id, name } = project
    editorData.updateHtmlFromRefers(id, {
      name
    })
  })

  referencedResourcePreviews.sprints?.forEach(sprint => {
    if (!sprint) return
    const { id, startDate, endDate, status } = sprint
    editorData.updateHtmlFromRefers(id, {
      startDate,
      endDate,
      status
    })
  })

  referencedResourcePreviews.users?.forEach(user => {
    if (!user) return
    const { id, email, pictureUrl, enabled, teamRolesString } = user
    editorData.updateHtmlFromRefers(id, {
      email,
      pictureUrl,
      enabled,
      teamRolesString
    })
  })
  return editorData.getHtmlText()
}
